import { formatTimestamp } from '../../../utils/timestamps';
import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    Chip,
    Paper,
    Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { fetchCellLineage, LINEAGE_FAILED_MESSAGE } from '../../../services/lineage';
import { formatText } from '../../common/TextFormatterProvider';
import { formatDisplayValue } from '../../../utils/tagValueUtils';
import { formatPeriodKey } from '../../../utils/periodUtils';
import LineageTimeline from './LineageTimeline';

/**
 * CellLineageDrawer - explains one cell of the Inspect grid.
 *
 * On a time-series table it shows the cell's current lineage (Spec 4 section 7),
 * read from the get_cell_lineage endpoint when the drawer opens: every file that
 * states the cell now, and how the column's policy chose among them
 * (LineageTimeline draws it). It describes the table as it is when opened, so it
 * can differ from a grid read made earlier.
 *
 * On a raw table, whose rows have no identifier, it shows where the clicked row
 * came from: the units its contributing-unit marker names, each with its file's
 * name, upload time and tags.
 *
 * `cellInfo` is, on a time-series table,
 *     { identifier, column, periodKey, period, sourcePeriod, currentValue }
 * where `column` is a detail or attribute column or a metric leaf by its dotted
 * path, `period` the clicked period of a metric cell (null for any other),
 * `periodKey` the period whose lineage is read: the period the value was
 * forward filled from (`sourcePeriod`) when it was, else `period`, and
 * `currentValue` the value as the grid shows it (DataTable.js::getCellValue).
 * On a raw table it is
 *     { contributingUnits, column, currentValue }.
 */
function CellLineageDrawer({ open, onClose, companyId, tableId, tableSchema, cellInfo }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // The endpoint's answer, on a time-series table.
    const [lineage, setLineage] = useState(null);
    // The clicked row's source units, on a raw table.
    const [rawSources, setRawSources] = useState(null);

    const { identifier, column, periodKey, period, sourcePeriod, currentValue, contributingUnits } = cellInfo;
    const isRaw = tableSchema.tableType === 'raw';

    useEffect(() => {
        if (!open) return undefined;

        // Closing the drawer or selecting another cell makes this read obsolete:
        // its answer is dropped rather than shown under the newer cell.
        let cancelled = false;

        const readLineage = async () => {
            setLoading(true);
            setError(null);
            setLineage(null);
            setRawSources(null);

            try {
                if (isRaw) {
                    // The units the row's marker names, then their files, for each
                    // unit's file name, upload time and tags.
                    const unitSnapshots = await Promise.all(contributingUnits.map((unitId) => getDoc(doc(
                        db, 'users', companyId, 'tables', tableId, 'units', unitId
                    ))));
                    if (cancelled) return;
                    const fileIds = [...new Set(unitSnapshots
                        .filter((unitSnapshot) => unitSnapshot.exists())
                        .map((unitSnapshot) => unitSnapshot.data().file_id))];
                    const fileSnapshots = await Promise.all(fileIds.map((fileId) => getDoc(doc(
                        db, 'users', companyId, 'tables', tableId, 'files', fileId
                    ))));
                    if (cancelled) return;
                    const filesById = new Map(fileSnapshots
                        .filter((fileSnapshot) => fileSnapshot.exists())
                        .map((fileSnapshot) => [fileSnapshot.id, fileSnapshot.data()]));

                    setRawSources(unitSnapshots.map((unitSnapshot, index) => {
                        const unitId = contributingUnits[index];
                        const unit = unitSnapshot.exists() ? unitSnapshot.data() : null;
                        const file = unit ? filesById.get(unit.file_id) : undefined;
                        // The unit, or its file, was deleted after the row was read.
                        if (!file) return { unitId, missing: true };
                        return {
                            unitId,
                            fileName: unit.file_name,
                            unitName: unit.name,
                            rowCount: unit.row_count,
                            uploadedAt: file.created_at,
                            tags: file.metadata,
                        };
                    }));
                } else {
                    const answer = await fetchCellLineage(companyId, tableId, identifier, column, periodKey);
                    if (cancelled) return;
                    setLineage(answer);
                }
            } catch (failure) {
                if (cancelled) return;
                console.error('Error reading the lineage of a cell:', failure);
                // A refusal from the endpoint carries its own sentence; any other
                // failure is not shown as it came.
                setError(failure.status ? failure.message : LINEAGE_FAILED_MESSAGE);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        readLineage();
        return () => {
            cancelled = true;
        };
    }, [open, companyId, tableId, isRaw, identifier, column, periodKey, contributingUnits]);

    const drawerWidth = { xs: '100%', sm: '450px', md: '500px' };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                zIndex: 1300,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{
                    p: 2,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {isRaw ? 'Row Source' : 'Current cell lineage'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatText(column)}
                            {period && ` • ${formatPeriodKey(period)}`}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Cell Info Summary */}
                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {identifier && (
                            <Chip
                                label={`ID: ${identifier}`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {isRaw && column && (
                            <Chip
                                label={formatText(column)}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {period && (
                            <Chip
                                label={formatPeriodKey(period)}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Current Value:{' '}
                        <Typography component="span" fontWeight={600} color="text.primary">
                            {currentValue === null || currentValue === undefined ? '-' : formatDisplayValue(currentValue)}
                        </Typography>
                    </Typography>
                    {sourcePeriod && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            This cell is forward filled from {formatPeriodKey(sourcePeriod)} - {formatText(column)}.
                        </Alert>
                    )}
                </Box>

                <Divider />

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : isRaw ? (
                        <Stack spacing={2}>
                            {rawSources.map((source) => (
                                <Paper key={source.unitId} variant="outlined" sx={{ p: 2 }}>
                                    {source.missing ? (
                                        <Typography color="text.secondary">
                                            Source upload no longer exists for unit {source.unitId}.
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {source.fileName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {source.unitName}
                                            </Typography>
                                            {source.uploadedAt && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Uploaded {formatTimestamp(source.uploadedAt)}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Rows appended in this upload: {source.rowCount ?? '-'}
                                            </Typography>
                                            {source.tags && Object.keys(source.tags).length > 0 && (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                                                    {Object.entries(source.tags).map(([key, value]) => (
                                                        <Chip
                                                            key={key}
                                                            size="small"
                                                            variant="outlined"
                                                            label={`${formatText(key)}: ${formatDisplayValue(value)}`}
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <LineageTimeline
                            lineage={lineage}
                            currentValue={currentValue}
                            companyId={companyId}
                            tableId={tableId}
                            table={tableSchema}
                        />
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}

export default CellLineageDrawer;
